import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Search, Printer, RefreshCw, X, ChevronLeft, ChevronRight, Repeat, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as greigeStorageApi from '@/services/greigeStorageApi';

const PAGE_SIZE = 50;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const AssortmentConversionReport = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalQty, setTotalQty] = useState(0);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchReport = useCallback(async (overrides = {}) => {
    const f = {
      page: overrides.page ?? page,
      search: overrides.search ?? search,
      date_from: overrides.date_from ?? dateFrom,
      date_to: overrides.date_to ?? dateTo,
    };
    setLoading(true);
    try {
      const params = { page: f.page, page_size: PAGE_SIZE };
      if (f.search) params.search = f.search;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      const data = await greigeStorageApi.getAssortmentConversionReport(params);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setTotalQty(data.total_qty || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load conversion report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, dateFrom, dateTo]);

  useEffect(() => { fetchReport({ page: 1 }); /* eslint-disable-next-line */ }, []);

  const applyFilters = () => fetchReport({ page: 1 });
  const clearFilters = () => {
    setSearch(''); setDateFrom(''); setDateTo('');
    fetchReport({ page: 1, search: '', date_from: '', date_to: '' });
  };

  const goPage = (p) => { if (p >= 1 && p <= totalPages) fetchReport({ page: p }); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await apiClient.get('/greige-storage/assortment/conversion-report', {
        params: {
          search: search || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          format: 'csv',
        },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `assortment_conversion_report_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const printReport = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const body = rows.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${r.pick_slip_number || '—'}</td>
        <td>${fmtDate(r.date)}</td>
        <td>${r.from_sort_no || '—'}${r.from_design ? `<div class="sub">${r.from_design}</div>` : ''}</td>
        <td>${r.to_sort_no || '—'}${r.to_design ? `<div class="sub">${r.to_design}</div>` : ''}</td>
        <td style="text-align:right">${r.qty ?? 0}</td>
      </tr>`).join('');
    win.document.write(`<!doctype html><html><head><title>Assortment Conversion Report</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#111}
        h1{font-size:18px;margin:0 0 4px}
        .meta{font-size:12px;color:#555;margin-bottom:14px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;vertical-align:top}
        th{background:#f3f4f6}
        .sub{color:#6b7280;font-size:10px;margin-top:2px}
        tfoot td{font-weight:bold;background:#f9fafb}
      </style></head><body>
      <h1>Assortment Design Conversion Report</h1>
      <div class="meta">Greige Storage · Generated ${new Date().toLocaleString('en-GB')} · ${total} row(s) · Total Qty ${totalQty}</div>
      <table><thead><tr>
        <th>#</th><th>Pick Slip Number</th><th>Date</th><th>From Sort No</th><th>To Sort No</th><th style="text-align:right">Qty</th>
      </tr></thead><tbody>${body}</tbody>
      <tfoot><tr><td colspan="5" style="text-align:right">Total Qty (this page)</td><td style="text-align:right">${rows.reduce((s, r) => s + (r.qty || 0), 0)}</td></tr></tfoot>
      </table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="assortment-conversion-report">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige-storage')} data-testid="acr-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Repeat className="h-6 w-6 text-fuchsia-600" /> Assortment Conversion Report
              </h1>
              <p className="text-sm text-gray-500">Design-changing assortment pick slips — source sort → target sort</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleExport} disabled={exporting || !rows.length}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="acr-export-csv-btn">
              <Download className="h-4 w-4 mr-2" /> {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
            <Button onClick={printReport} variant="outline" data-testid="acr-print-btn" disabled={!rows.length}>
              <Printer className="h-4 w-4 mr-2" /> Print
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card><CardContent className="p-4">
            <div className="text-xs text-gray-500">Conversion Rows</div>
            <div className="text-2xl font-bold text-gray-900" data-testid="acr-stat-rows">{total}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-gray-500">Total Qty Converted</div>
            <div className="text-2xl font-bold text-fuchsia-600" data-testid="acr-stat-qty">{totalQty}</div>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <Card className="mb-4"><CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium text-gray-600">Search (slip / sort no / design)</label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="Search…" data-testid="acr-search-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">From</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="acr-date-from" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">To</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="acr-date-to" />
            </div>
            <div className="flex gap-2">
              <Button onClick={applyFilters} data-testid="acr-apply-btn"><Search className="h-4 w-4 mr-1" /> Apply</Button>
              <Button variant="outline" onClick={clearFilters} data-testid="acr-clear-btn"><X className="h-4 w-4 mr-1" /> Clear</Button>
              <Button variant="ghost" size="icon" onClick={() => fetchReport()} data-testid="acr-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardContent></Card>

        {/* Table */}
        <Card><CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pick Slip Number</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>From Sort No</TableHead>
                <TableHead>To Sort No</TableHead>
                <TableHead className="text-right">Qty</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-gray-500">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-gray-500" data-testid="acr-empty">No conversions found</TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={`${r.pick_slip_number}-${r.from_sort_no}-${r.to_sort_no}-${i}`} data-testid={`acr-row-${i}`}>
                  <TableCell className="font-mono text-xs" data-testid={`acr-slip-${i}`}>{r.pick_slip_number || '—'}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{r.from_sort_no || '—'}</div>
                    {r.from_design && <div className="text-xs text-gray-500">{r.from_design}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <ArrowRight className="h-3.5 w-3.5 text-fuchsia-500" />
                      <span className="font-medium">{r.to_sort_no || '—'}</span>
                    </div>
                    {r.to_design && <div className="text-xs text-gray-500 pl-4">{r.to_design}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge className="bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200" data-testid={`acr-qty-${i}`}>{r.qty ?? 0}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500" data-testid="acr-page-info">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => goPage(page - 1)} disabled={page <= 1} data-testid="acr-prev-btn">
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" onClick={() => goPage(page + 1)} disabled={page >= totalPages} data-testid="acr-next-btn">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssortmentConversionReport;
